import { Button, Dialog, DialogContent, Divider, Stack, Typography } from "@mui/material";
import { useCallback, useMemo } from "react";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import type { ApiResponse } from "@entrilia/shared/api/types.ts";
import PromptDialogHeader from "@entrilia/shared/components/dialog/PromptDialogHeader.tsx";
import DialogButtons from "@entrilia/shared/components/DialogButtons.tsx";
import LoaderBox from "@entrilia/shared/components/LoaderBox.tsx";
import useFetch from "@entrilia/shared/hooks/useFetch.ts";
import type {
  GetReportAuthorizationResponse,
  ReportAuthorization,
  ReportGroup,
} from "../../../../../../../../api/types/svTypes.ts";
import CompanySharingRow from "./CompanySharingRow.tsx";
import { useSharingGroupContext } from "./contexts/SharingGroupContext.tsx";
import { SharingGroupContextProvider } from "./contexts/SharingGroupContextProvider.tsx";
import type { ReportCompany } from "./ShareReport.types.ts";
import AuthorizedGroupsList from "./userGroups/AuthorizedGroupsList.tsx";
import { buildAuthorizedGroupItems, buildAuthorizedUserItems } from "./userGroups/authorizedItems.ts";
import AuthorizedUsersList from "./userGroups/AuthorizedUsersList.tsx";
import UserSelection from "./UserSelection.tsx";

interface Props {
  clientCode: string;
  groupId: string;
  groupCaption: string;
  company: ReportCompany;
  currentUserId: string;
  getSharing: (clientCode: string, groupId: string) => Promise<ApiResponse<GetReportAuthorizationResponse>>;
  updateSharing: (
    clientCode: string,
    groupId: string,
    authorization: ReportAuthorization
  ) => Promise<ApiResponse<ReportGroup>>;
  onClose: () => void;
}

export default function ShareReportGroup({
  clientCode,
  groupId,
  groupCaption,
  company,
  currentUserId,
  getSharing,
  updateSharing,
  onClose,
}: Props) {
  const fetchSharing = useCallback(() => getSharing(clientCode, groupId), [getSharing, clientCode, groupId]);
  const [data, error, { isFetching }] = useFetch(fetchSharing);

  return (
    <Dialog
      open
      maxWidth="sm"
      fullWidth
      sx={{ ".MuiDialog-container": { alignItems: "start", pt: 20 } }}
      slotProps={{ paper: { sx: { minHeight: 250 } } }}
    >
      <PromptDialogHeader text={`Share group ${groupCaption}`} onClose={onClose} />
      <LoaderBox loading={isFetching} overlay>
        {error && (
          <Typography sx={{ p: 2 }}>
            {error.type === "NotEnoughPermission"
              ? "You don't have permission to manage this group's sharing."
              : error.message || "Failed to load the group's sharing."}
          </Typography>
        )}
        {data && !error && (
          <SharingGroupContextProvider
            clientCode={clientCode}
            groupId={groupId}
            company={company}
            currentUserId={currentUserId}
            defaultAuthorization={data.authorization}
            users={data.users}
            groups={data.groups}
            updateSharing={updateSharing}
            onClose={onClose}
          >
            <ShareReportGroupContent onClose={onClose} />
          </SharingGroupContextProvider>
        )}
      </LoaderBox>
    </Dialog>
  );
}

function ShareReportGroupContent({ onClose }: { onClose: () => void }) {
  const {
    company,
    currentUserId,
    users,
    groups,
    availableNotAuthorizedUsers,
    companyAccess,
    setCompanyAccess,
    authorizedUsers,
    authorizedGroups,
    isChanged,
    isSaving,
    onUsersSelected,
    onGroupsSelected,
    onUserAccessChanged,
    onGroupAccessChanged,
    onRemoveUser,
    onRemoveGroup,
    onSave,
  } = useSharingGroupContext();

  const authorizedGroupItems = useMemo(
    () => buildAuthorizedGroupItems(authorizedGroups, groups),
    [authorizedGroups, groups]
  );
  const authorizedUserItems = useMemo(
    () => buildAuthorizedUserItems(authorizedUsers, users, currentUserId, ReportAccessType.FullAccess),
    [authorizedUsers, users, currentUserId]
  );
  const hasMembersWithAccess = authorizedGroupItems.length > 0 || authorizedUserItems.length > 0;

  return (
    <>
      <DialogContent sx={{ pt: 0.5 }}>
        <Stack spacing={2} useFlexGap sx={{ width: "100%" }}>
          <CompanySharingRow company={company} access={companyAccess} disabled={isSaving} onSelect={setCompanyAccess} />
          <UserSelection
            disabled={isSaving}
            availableUsers={availableNotAuthorizedUsers}
            availableGroups={groups}
            authorizedGroups={authorizedGroups}
            currentUserId={currentUserId}
            authorizedUsers={authorizedUsers}
            onUsersSelected={onUsersSelected}
            onGroupsSelected={onGroupsSelected}
          />
        </Stack>
      </DialogContent>
      {hasMembersWithAccess && (
        <>
          <Divider />
          <DialogContent sx={{ pt: 1 }}>
            <Typography variant="caption" color="textSecondary">
              Members with access
            </Typography>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <AuthorizedGroupsList
                items={authorizedGroupItems}
                disabled={isSaving}
                onAccessChanged={onGroupAccessChanged}
                onRemoveGroup={onRemoveGroup}
              />
              <AuthorizedUsersList
                items={authorizedUserItems}
                disabled={isSaving}
                onAccessChanged={onUserAccessChanged}
                onRemoveUser={onRemoveUser}
              />
            </Stack>
          </DialogContent>
        </>
      )}
      <Divider />
      <DialogButtons onCancel={onClose}>
        <Button variant="contained" color="primary" disabled={!isChanged} loading={isSaving} onClick={onSave}>
          Apply
        </Button>
      </DialogButtons>
    </>
  );
}
